import { usersGetAuthenticated } from 'api/endpoints/jelledFromFileSpecWithTransformer'
import { authenticatedUserAtom, organizationFamily } from 'state'
import { useRecoilState, useRecoilTransaction_UNSTABLE, useRecoilValue } from 'recoil'
import { useLayoutEffect, useMemo, useState } from 'react'
import { useAuthToken } from 'hooks/use-auth-token'
import axios from 'axios'
import { OrgUserRole, OrganizationFull } from 'api/model'
import { useActiveOrganization } from './use-organization'
import { customerOrgProfiles } from 'utils'
import Bugsnag from '@bugsnag/js'
import { LOCAL_STORAGE } from 'app-constants'

function useFetchAuthenticatedUser() {
  const [authToken, setAuthToken] = useAuthToken()
  const [user, setUser] = useRecoilState(authenticatedUserAtom)
  const [, setActiveOrganization] = useActiveOrganization()

  const [isLoading, setIsLoading] = useState(false)

  const setOrganizations = useRecoilTransaction_UNSTABLE(
    ({ set }) =>
      (organizations: OrganizationFull[]) => {
        organizations.forEach((o) => {
          set(organizationFamily(o.id), (currentOrganization) => Object.assign({}, currentOrganization, o))
        })
      },
    []
  )

  useLayoutEffect(() => {
    async function fetchData() {
      setIsLoading(true)

      try {
        const response = await usersGetAuthenticated()

        if (!localStorage.getItem(LOCAL_STORAGE.IMPERSONATING_AUTH_TOKEN) && response.data.token) {
          // If we're not impersonating, and the backend sends an updated auth token, store it.
          // This can happen if the backend changes scopes for the user.
          setAuthToken(response.data.token)
        }

        if ('profiles' in response.data) {
          setOrganizations(response.data.profiles.map((up) => up.organization))
          setUser(response.data)
        }

        setActiveOrganization((currentActiveOrganization) => {
          if (
            currentActiveOrganization &&
            'profiles' in response.data &&
            response.data.profiles.some((up) => up.organization.id === currentActiveOrganization)
          ) {
            return currentActiveOrganization
          }

          if ('profiles' in response.data && customerOrgProfiles(response.data).length > 1) {
            return null
          }

          return 'profiles' in response.data ? response.data.organization_id ?? null : null
        })

        if (Bugsnag.isStarted()) {
          Bugsnag.setUser(response.data.id, response.data.email, response.data.name)
        }
      } catch (error) {
        if (axios.isAxiosError(error) && (error.response?.status ?? 0) >= 400) {
          setAuthToken(null)
          setActiveOrganization(null)
          return
        }

        // TODO: handle other errors
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [authToken, setUser, setAuthToken, setActiveOrganization, setOrganizations])

  return {
    user,
    isLoading,
    setUser,
  }
}

function useUserIsAdmin() {
  const [activeOrganization] = useActiveOrganization()
  const authenticatedUser = useRecoilValue(authenticatedUserAtom)
  const activeProfile = useMemo(
    () => authenticatedUser?.profiles.find((up) => up.organization.id === activeOrganization),
    [activeOrganization, authenticatedUser?.profiles]
  )

  if (!authenticatedUser || !activeProfile) {
    return false
  }

  return activeProfile.role === OrgUserRole.admin
}

export { useFetchAuthenticatedUser, useUserIsAdmin }
